import { AgentStreamRequest } from '@Models';
import { getAccessToken } from '@Store';
import { StreamingStatus } from './enums';
import { MessageConfig, StreamingStateConfig } from './types';

/**
 * Sets up the initial state for a new streaming request
 * @param {StreamingStateConfig} streamingState - Streaming state configuration
 */
export function setupStreamingRequest(streamingState: StreamingStateConfig): void {
  const {
    setStreamingStatus,
    setStreamingContent,
    setIsThinking,
    isThinkingRef,
    setThinkingMessage,
    setThinkingSteps,
    seenEventTypesRef,
    initialThinkingMessage,
  } = streamingState;

  setStreamingStatus(StreamingStatus.CONNECTING);
  setStreamingContent('');
  setIsThinking(true);
  // Allow reassignment in refs
  // eslint-disable-next-line no-param-reassign
  isThinkingRef.current = true;
  setThinkingMessage(initialThinkingMessage);
  setThinkingSteps?.([]);
  seenEventTypesRef.current.clear();
}

/**
 * Processes incoming chunk during streaming
 */
export function processStreamingChunk(
  chunk: string,
  hasReceivedContent: boolean,
  finalContent: string,
  conversationId: string | undefined,
  streamingState: StreamingStateConfig,
  messageConfig: MessageConfig,
): { hasReceivedContent: boolean; finalContent: string } {
  const {
    setStreamingStatus,
    setIsThinking,
    isThinkingRef,
    setThinkingMessage,
    seenEventTypesRef,
    setStreamingContent,
  } = streamingState;
  const { receiveAssistantMessage, updateStreamingMessage } = messageConfig;

  setStreamingStatus(StreamingStatus.STREAMING);

  let newHasReceivedContent = hasReceivedContent;
  let newFinalContent = finalContent;

  // End thinking animation when content starts
  if (!hasReceivedContent) {
    setIsThinking(false);
    isThinkingRef.current = false;
    setThinkingMessage('');
    seenEventTypesRef.current.clear();

    // First chunk - create message and render directly.
    // With streamFinalResponse: True, the agent streams small incremental chunks
    // (token-by-token), so simulated typewriter animation is unnecessary —
    // the natural chunk arrival rate provides progressive rendering.
    newHasReceivedContent = true;
    newFinalContent = chunk;

    receiveAssistantMessage('', undefined, conversationId ?? undefined);
    updateStreamingMessage(newFinalContent, conversationId ?? undefined);
  } else {
    // Additional chunks - append and render directly
    newFinalContent += chunk;
    updateStreamingMessage(newFinalContent, conversationId ?? undefined);
  }

  setStreamingContent(newFinalContent);

  return { hasReceivedContent: newHasReceivedContent, finalContent: newFinalContent };
}

// ============================================================================
// Low-level streaming functions (SSE and NDJSON)
// ============================================================================

// Types
/**
 * Represents structured SSE event data
 */
interface SSEEventData {
  readonly eventId: string;
  readonly eventType: string;
  readonly data: string;
}

/**
 * Represents a parsed SSE line with type and value
 */
interface SSELineData {
  readonly type: 'id' | 'event' | 'data';
  readonly value: string;
}

/**
 * Represents fallback event data for unknown event types
 */
interface FallbackEventData {
  readonly type?: string;
  readonly text?: string;
  readonly data?: string;
}

/**
 * Supported SSE event types
 */
type SSEEventType = 'chunk' | 'trace' | 'error' | 'done';

// Constants
/** SSE event separator used to delimit events */
const SSE_EVENT_SEPARATOR = '\n\n' as const;
/** Length of the SSE event separator */
const SSE_EVENT_SEPARATOR_LENGTH = 2 as const;
/** Default conversation ID for agent stream requests */
const DEFAULT_CONVERSATION_ID = 'default-conversation' as const;
/** Default maximum prompt length for agent stream requests */
const DEFAULT_MAX_PROMPT_LENGTH = 10000 as const;
/** SSE line prefixes for parsing */
const SSE_LINE_PREFIXES = {
  ID: 'id:',
  EVENT: 'event:',
  DATA: 'data:',
} as const;
/** Stream endpoint for the agent runner */
const STREAM_ENDPOINT = 'agent/aws-runner/run/stream' as const;

/**
 * Creates the request body for agent streaming requests
 * @param request - The agent stream request parameters
 * @returns Formatted request body with default values applied
 */
const createRequestBody = (request: AgentStreamRequest) => ({
  prompt: request.prompt,
  conversation_id: request.conversation_id || DEFAULT_CONVERSATION_ID,
  max_prompt_length: request.max_prompt_length || DEFAULT_MAX_PROMPT_LENGTH,
  trace: true,
  source_name: request.source_name,
});

/**
 * Parses a single SSE line to extract type and value
 * @param line - The SSE line to parse (e.g., "data: hello world")
 * @returns Parsed line data or null if line doesn't match SSE format
 */
const parseSSELine = (line: string): SSELineData | null => {
  if (line.startsWith(SSE_LINE_PREFIXES.ID)) {
    return { type: 'id', value: line.substring(SSE_LINE_PREFIXES.ID.length).trim() };
  }
  if (line.startsWith(SSE_LINE_PREFIXES.EVENT)) {
    return { type: 'event', value: line.substring(SSE_LINE_PREFIXES.EVENT.length).trim() };
  }
  if (line.startsWith(SSE_LINE_PREFIXES.DATA)) {
    return { type: 'data', value: line.substring(SSE_LINE_PREFIXES.DATA.length) };
  }
  return null;
};

/**
 * Parses a complete SSE event from raw event text
 * @param eventText - Raw SSE event text containing id, event, and data lines
 * @returns Structured SSE event data
 */
const parseSSEEvent = (eventText: string): SSEEventData => {
  const lines = eventText.split('\n');

  const eventData = lines.reduce(
    (acc, line) => {
      const parsed = parseSSELine(line);
      if (!parsed) return acc;

      switch (parsed.type) {
        case 'id':
          return { ...acc, eventId: parsed.value };
        case 'event':
          return { ...acc, eventType: parsed.value };
        case 'data':
          return { ...acc, dataLines: [...acc.dataLines, parsed.value] };
        default:
          return acc;
      }
    },
    { eventId: '', eventType: '', dataLines: [] as string[] },
  );

  return {
    eventId: eventData.eventId,
    eventType: eventData.eventType,
    data: eventData.dataLines.join('\n'),
  };
};

/**
 * Handles chunk events by calling the onChunk callback
 * @param data - The chunk data received from the stream
 * @param onChunk - Callback function to handle chunk data
 */
const handleChunkEvent = (data: string, onChunk: (text: string) => void): void => {
  onChunk(data);
};

/**
 * Handles trace events by parsing JSON data and calling the onTrace callback
 * @param data - The trace data received from the stream
 * @param onTrace - Optional callback function to handle trace data
 */
const handleTraceEvent = (data: string, onTrace?: (trace: unknown) => void): void => {
  if (!onTrace) return;

  try {
    const traceData: unknown = JSON.parse(data);
    onTrace(traceData);
  } catch {
    onTrace(data);
  }
};

/**
 * Handles error events by throwing an error with the received message
 * @param data - The error message received from the stream
 * @throws {Error} Always throws an error with the received message
 */
const handleErrorEvent = (data: string): never => {
  // Try to parse the error message if it's a JSON string
  let errorMessage = data || 'Streaming error';

  try {
    // The error data might be a JSON string, try to parse it
    const parsed: unknown = JSON.parse(data);
    errorMessage = typeof parsed === 'string' ? parsed : data;
  } catch {
    // If parsing fails, use the raw data
    errorMessage = data;
  }

  throw new Error(errorMessage);
};

/**
 * Handles fallback events for unknown event types by attempting to parse as JSON
 * @param data - The fallback data received from the stream
 * @param onChunk - Callback function to handle chunk data if parsing succeeds
 */
const handleFallbackEvent = (data: string, onChunk: (text: string) => void): void => {
  if (!data) return;

  try {
    const parsed = JSON.parse(data) as FallbackEventData;
    if (parsed?.type === 'chunk') {
      onChunk(parsed.text || parsed.data || '');
    }
  } catch {
    // Silently ignore parsing errors for fallback
  }
};

/**
 * Processes a complete SSE event by routing it to the appropriate handler
 * @param eventText - The complete SSE event text
 * @param onChunk - Callback function for chunk events
 * @param onTrace - Optional callback function for trace events
 */
const processSSEEvent = (
  eventText: string,
  onChunk: (text: string) => void,
  onTrace?: (trace: unknown) => void,
): void => {
  const { eventType, data } = parseSSEEvent(eventText);
  const eventTypeAsEnum = eventType as SSEEventType;

  switch (eventTypeAsEnum) {
    case 'chunk':
      handleChunkEvent(data, onChunk);
      break;
    case 'trace':
      handleTraceEvent(data, onTrace);
      break;
    case 'error':
      handleErrorEvent(data);
      break;
    case 'done':
      return;
    default:
      handleFallbackEvent(data, onChunk);
      break;
  }
};

/**
 * Extracts a complete SSE event from the buffer
 * @param buffer - The current buffer containing partial SSE events
 * @returns Object with complete event text and remaining buffer, or null if no complete event found
 */
const extractCompleteEvent = (buffer: string): { eventText: string; remainingBuffer: string } | null => {
  const eventEndIndex = buffer.indexOf(SSE_EVENT_SEPARATOR);

  if (eventEndIndex === -1) {
    return null;
  }

  return {
    eventText: buffer.substring(0, eventEndIndex),
    remainingBuffer: buffer.substring(eventEndIndex + SSE_EVENT_SEPARATOR_LENGTH),
  };
};

/**
 * Processes the buffer to extract and handle complete SSE events
 * @param buffer - The current buffer containing partial SSE events
 * @param onChunk - Callback function for chunk events
 * @param onTrace - Optional callback function for trace events
 * @returns The remaining buffer after processing complete events
 */
const processBuffer = (buffer: string, onChunk: (text: string) => void, onTrace?: (trace: unknown) => void): string => {
  let remainingBuffer = buffer;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const result = extractCompleteEvent(remainingBuffer);

    if (!result) {
      break;
    }

    const { eventText, remainingBuffer: newBuffer } = result;
    remainingBuffer = newBuffer;

    if (eventText.trim()) {
      processSSEEvent(eventText, onChunk, onTrace);
    }
  }

  return remainingBuffer;
};

/**
 * Creates fetch options for the SSE request
 * @param accessToken - The authentication token
 * @param requestBody - The request body to send
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Fetch options configured for SSE
 */
const createFetchOptions = (accessToken: string, requestBody: object, signal?: AbortSignal): RequestInit => ({
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    Authorization: `Bearer ${accessToken}`,
  },
  body: JSON.stringify(requestBody),
  signal,
});

/**
 * Validates the HTTP response for errors
 * @param response - The fetch response to validate
 * @throws {Error} Throws an error if the response is not ok
 */
const validateResponse = (response: Response): void => {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
};

/**
 * Gets the stream reader from the response body
 * @param response - The fetch response containing the stream
 * @returns The readable stream reader
 * @throws {Error} Throws an error if no response body is available
 */
const getStreamReader = (response: Response): ReadableStreamDefaultReader<Uint8Array> => {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body available for streaming');
  }
  return reader;
};

/**
 * Processes stream chunks by reading from the stream and handling SSE events
 * @param reader - The stream reader to read chunks from
 * @param decoder - Text decoder to convert bytes to text
 * @param onChunk - Callback function for chunk events
 * @param onTrace - Optional callback function for trace events
 */
const processStreamChunks = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  decoder: TextDecoder,
  onChunk: (text: string) => void,
  onTrace?: (trace: unknown) => void,
): Promise<void> => {
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    const chunk = decoder.decode(value, { stream: true });
    buffer += chunk;

    buffer = processBuffer(buffer, onChunk, onTrace);
  }
};

/**
 * Main function to stream agent responses using Server-Sent Events.
 * Establishes a connection, processes the stream, and calls appropriate callbacks.
 *
 * @param baseApiAddress - The base API address for the request
 * @param request - The agent stream request parameters
 * @param onChunk - Callback function called for each text chunk received
 * @param onTrace - Optional callback function called for trace data
 * @param signal - Optional AbortSignal to cancel the request
 * @throws {Error} When the connection fails, response is invalid, or streaming errors occur
 */
const streamAgent = async (
  baseApiAddress: string,
  request: AgentStreamRequest,
  onChunk: (text: string) => void,
  onTrace?: (trace: unknown) => void,
  signal?: AbortSignal,
): Promise<void> => {
  const accessToken = await getAccessToken();
  const requestBody = createRequestBody(request);
  const fetchOptions = createFetchOptions(accessToken, requestBody, signal);

  const response = await fetch(`${baseApiAddress}${STREAM_ENDPOINT}`, fetchOptions);
  validateResponse(response);

  const reader = getStreamReader(response);
  const decoder = new TextDecoder();

  try {
    await processStreamChunks(reader, decoder, onChunk, onTrace);
  } finally {
    reader.releaseLock();
  }
};

/**
 * Structured event from NDJSON stream - Chunk type
 */
export interface StreamEventChunk {
  readonly type: 'chunk';
  readonly text: string;
}

/**
 * Structured event from NDJSON stream - Trace type
 * Backend now sends processed event_type and translated message
 */
export interface StreamEventTrace {
  readonly type: 'trace';
  readonly event_type: string;
  readonly message: string;
  readonly input_tokens?: number;
  readonly output_tokens?: number;
}

/**
 * Structured event from NDJSON stream - Done type
 */
export interface StreamEventDone {
  readonly type: 'done';
  readonly metrics: {
    input_tokens: number;
    output_tokens: number;
    latency: number;
    cached?: boolean;
  };
}

/**
 * Structured event from NDJSON stream - Error type
 */
export interface StreamEventError {
  readonly type: 'error';
  readonly error: string;
}

/**
 * Structured event from NDJSON stream - Files type.
 * AgentCore emits files already on S3 (s3_uri present).
 */
export interface StreamEventFile {
  readonly name: string;
  readonly type?: string | null;
  readonly size?: number;
  readonly s3_uri?: string;
  readonly s3_key?: string;
  readonly s3_bucket?: string;
}
export interface StreamEventFiles {
  readonly type: 'files';
  readonly files: ReadonlyArray<StreamEventFile>;
}

/**
 * Structured event from NDJSON stream - Options type
 * Backend extracted a `<bops:options>` block from the agent's text and is
 * delivering the parsed items so the frontend can render clickable cards.
 */
export interface StreamEventChoice {
  readonly id: string;
  readonly label: string;
}
export interface StreamEventOptionItem {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  /** Only on `choicegrid` rows: the answers offered for that row. */
  readonly choices?: ReadonlyArray<StreamEventChoice>;
}
export interface StreamEventOptions {
  readonly type: 'options';
  readonly items: ReadonlyArray<StreamEventOptionItem>;
}

/**
 * Structured event from NDJSON stream - Multi-select type
 * Backend extracted a `<bops:multiselect>` block; the frontend renders
 * checkboxes with a confirm button (same item shape as options).
 */
export interface StreamEventMultiselect {
  readonly type: 'multiselect';
  readonly items: ReadonlyArray<StreamEventOptionItem>;
}

/**
 * Structured event from NDJSON stream - Choice grid type
 * Backend extracted a `<bops:choicegrid>` block: one question asked of many
 * subjects, each row rendered with its own answer set plus a confirm button.
 */
export interface StreamEventChoicegrid {
  readonly type: 'choicegrid';
  readonly items: ReadonlyArray<StreamEventOptionItem>;
}

/**
 * Union type of all possible stream events
 */
export type StreamEvent =
  | StreamEventChunk
  | StreamEventTrace
  | StreamEventDone
  | StreamEventError
  | StreamEventFiles
  | StreamEventOptions
  | StreamEventMultiselect
  | StreamEventChoicegrid;

/**
 * Async generator that streams agent events via NDJSON.
 * This is a simpler alternative to SSE for consuming streaming events.
 *
 * @param baseApiAddress - The base API address
 * @param request - The agent stream request parameters
 * @param signal - Optional AbortSignal for request cancellation
 * @yields {StreamEvent} Stream events in NDJSON format
 * @throws {Error} When connection fails or streaming errors occur
 */
export async function* streamAgentNDJSON(
  baseApiAddress: string,
  request: AgentStreamRequest,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent, void, undefined> {
  const accessToken = await getAccessToken();

  const response = await fetch(`${baseApiAddress}agent/aws-runner/run/stream-json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      prompt: request.prompt,
      conversation_id: request.conversation_id || DEFAULT_CONVERSATION_ID,
      max_prompt_length: request.max_prompt_length || DEFAULT_MAX_PROMPT_LENGTH,
      trace: true,
      source_name: request.source_name,
      display_text: request.display_text,
      context_file: request.context_file,
      selected_journey: request.selected_journey,
      journey_cache_file: request.journey_cache_file,
      interaction_timestamp: request.interaction_timestamp,
    }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body available for streaming');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      // Process each complete line using while loop to comply with ESLint rules
      let lineIndex = 0;
      while (lineIndex < lines.length) {
        const trimmedLine = lines[lineIndex].trim();
        if (trimmedLine) {
          try {
            const event = JSON.parse(trimmedLine) as StreamEvent;

            if (event.type === 'error') {
              throw new Error(event.error || 'Streaming error');
            }

            yield event;
          } catch (parseError) {
            if (parseError instanceof SyntaxError) {
              // eslint-disable-next-line no-console
              console.warn('Failed to parse NDJSON line:', trimmedLine);
            } else {
              throw parseError;
            }
          }
        }
        lineIndex += 1;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ============================================================================
// Streaming animation buffer (requestAnimationFrame-based)
// ============================================================================

/**
 * Creates a streaming animator that decouples network chunk timing from render timing.
 *
 * Instead of rendering chunks directly to the DOM as they arrive (which produces
 * choppy/"pegado" text appearance), this buffers incoming text and reveals it
 * at a smooth 60fps rate using requestAnimationFrame.
 *
 * Adaptive speed:
 * - remaining > 200 chars → ~40 chars/frame (fast drain for large buffers)
 * - remaining 50-200      → ~15 chars/frame (medium)
 * - remaining < 50        → ~3-6 chars/frame (slow, natural feel)
 *
 * @param onRender - Callback invoked each frame with the visible text so far
 * @param signal - Optional AbortSignal; when aborted, the animator cancels automatically
 * @returns Control object with update, waitForCompletion, flush, and cancel methods
 */
export function createStreamingAnimator(onRender: (visibleText: string) => void, signal?: AbortSignal) {
  let fullText = '';
  let displayedLength = 0;
  let frameId: number | null = null;
  let cancelled = false;
  let resolveCompletion: (() => void) | null = null;
  let completionPromise: Promise<void> | null = null;
  let streamDone = false;

  function doCancel() {
    cancelled = true;
    if (frameId) {
      cancelAnimationFrame(frameId);
      frameId = null;
    }
    if (resolveCompletion) {
      resolveCompletion();
      resolveCompletion = null;
    }
  }

  // Auto-cancel when the AbortSignal fires (stop generation / unmount)
  if (signal) {
    signal.addEventListener('abort', doCancel, { once: true });
  }

  function getCharsPerFrame(): number {
    const remaining = fullText.length - displayedLength;
    // After stream ends, use moderate speed so large responses
    // (arriving as a single chunk) render in ~4-5 seconds.
    if (streamDone) {
      if (remaining > 500) return 12;
      if (remaining > 200) return 8;
      if (remaining > 50) return 4;
      return Math.max(2, Math.ceil(remaining / 10));
    }
    // During active streaming, drain at full speed
    if (remaining > 200) return 40;
    if (remaining > 50) return 15;
    return Math.max(3, Math.ceil(remaining / 10));
  }

  function frame() {
    if (cancelled) {
      resolveCompletion?.();
      resolveCompletion = null;
      return;
    }

    if (displayedLength < fullText.length) {
      const chars = getCharsPerFrame();
      displayedLength = Math.min(displayedLength + chars, fullText.length);
      onRender(fullText.substring(0, displayedLength));
      frameId = requestAnimationFrame(frame);
    } else {
      frameId = null;
      // Buffer caught up — if stream is done, resolve completion
      if (streamDone && resolveCompletion) {
        resolveCompletion();
        resolveCompletion = null;
      }
    }
  }

  return {
    /** Feed new accumulated text into the buffer (called on each chunk) */
    update(newFullText: string) {
      fullText = newFullText;
      if (!frameId && !cancelled) {
        frameId = requestAnimationFrame(frame);
      }
    },

    /** Returns a promise that resolves when all buffered text has been rendered */
    waitForCompletion(): Promise<void> {
      streamDone = true;
      if (displayedLength >= fullText.length) {
        return Promise.resolve();
      }
      if (!completionPromise) {
        completionPromise = new Promise<void>((resolve) => {
          resolveCompletion = resolve;
        });
      }
      // Kick the loop in case it paused
      if (!frameId && !cancelled) {
        frameId = requestAnimationFrame(frame);
      }
      return completionPromise;
    },

    /** Reveal all remaining text instantly (for error recovery) */
    flush() {
      if (cancelled) return;
      if (frameId) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
      displayedLength = fullText.length;
      onRender(fullText);
      if (resolveCompletion) {
        resolveCompletion();
        resolveCompletion = null;
      }
    },

    /** Stop animation immediately, cancel pending frame, resolve promise */
    cancel: doCancel,
  };
}

export { streamAgent as streamAgentSSE };
export default streamAgent;
